import LogoDefensoria from '/img/LOGO_DPE_2024_ok.png';
import type { ListAllIntershipsOfTheMonthResponseDto } from '@/api.types';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import { styles } from './AttendanceSheetsPerMonthPDFstyle';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AttendanceSheetStatusEnum, getAttendanceSheetStatusEnumOptions } from '../../enums/attendance-sheet-status.enum';

export interface AttendanceSheetsPerMonthPdfProps {
  mesBusca: Date;
  registrosVisiveis: ListAllIntershipsOfTheMonthResponseDto[];
  totalRegistros: number;
  visibleColumnKeys?: string[] | null;
}

function formatarMesAno(date: Date) {
  const texto = format(new Date(date.getFullYear(), date.getMonth(), 1), 'MMMM / yyyy', { locale: ptBR });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function getStatusIcon(status?: number | null): { symbol: string; color: string; label: string } {
  const resolvedLabel =
    (status
      ? getAttendanceSheetStatusEnumOptions().find((o) => Number(o.value) === Number(status))?.label
      : undefined) ??
    '-';

  switch (Number(status)) {
    case AttendanceSheetStatusEnum.EM_PREENCHIMENTO:
      return { symbol: 'P', color: 'orange', label: resolvedLabel };
    case AttendanceSheetStatusEnum.AGUARDANDO_ASSINATURA:
      return { symbol: 'S', color: 'dodgerblue', label: resolvedLabel };
    case AttendanceSheetStatusEnum.AGUARDANDO_REVISAO:
      return { symbol: 'R', color: 'seagreen', label: resolvedLabel };
    case AttendanceSheetStatusEnum.CONCLUIDA:
      return { symbol: 'C', color: 'blue', label: resolvedLabel };
    case AttendanceSheetStatusEnum.EM_CORRECAO:
      return { symbol: 'X', color: 'lightsalmon', label: resolvedLabel };
    default:
      return { symbol: '?', color: 'gray', label: resolvedLabel };
  }
}

function formatarEditadoEm(row: ListAllIntershipsOfTheMonthResponseDto) {
  if (!row.folha?.editadoEm) return '-';
  try {
    return new Date(row.folha.editadoEm).toLocaleDateString('pt-BR');
  } catch {
    return '-';
  }
}

export function AttendanceSheetsPerMonthPdf({
  mesBusca,
  registrosVisiveis,
  totalRegistros,
  visibleColumnKeys,
}: AttendanceSheetsPerMonthPdfProps) {
  const referencia = isNaN(mesBusca.getTime()) ? '-' : formatarMesAno(mesBusca);
  const showResumoLinha = registrosVisiveis.length !== totalRegistros;

  const columnsCatalog = [
    {
      key: 'referencia',
      header: 'REFERÊNCIA',
      weight: 2,
      value: () => referencia,
    },
    {
      key: 'estagiario.cpf',
      header: 'CPF',
      weight: 2,
      value: (row: ListAllIntershipsOfTheMonthResponseDto) => row.estagiario?.cpf ?? '-',
    },
    {
      key: 'estagiario.nome',
      header: 'ESTAGIÁRIO(A)',
      weight: 4,
      value: (row: ListAllIntershipsOfTheMonthResponseDto) => row.estagiario?.nome ?? '-',
    },
    {
      key: 'estagio.unidade',
      header: 'UNIDADE/SETOR',
      weight: 4,
      value: (row: ListAllIntershipsOfTheMonthResponseDto) => row.estagio?.unidade ?? '-',
    },
    {
      key: 'responsavel.nome',
      header: 'RESPONSÁVEL',
      weight: 4,
      value: (row: ListAllIntershipsOfTheMonthResponseDto) => row.responsavel?.nome ?? '-',
    },
    {
      key: 'status',
      header: 'STATUS',
      weight: 1,
      value: (row: ListAllIntershipsOfTheMonthResponseDto) => getStatusIcon(row.folha?.status),
      isStatus: true,
    },
    {
      key: 'folha.editadoEm',
      header: 'EDITADO',
      weight: 2,
      value: (row: ListAllIntershipsOfTheMonthResponseDto) => formatarEditadoEm(row),
    },
  ] as const;

  const sanitizedVisibleKeys =
    (visibleColumnKeys ?? []).filter((k) => k !== 'acoes' && k !== 'acoes+') ?? [];

  const defaultKeys = ['estagiario.nome', 'estagio.unidade', 'responsavel.nome', 'status', 'folha.editadoEm'];
  const activeKeys = (sanitizedVisibleKeys.length > 0 ? sanitizedVisibleKeys : defaultKeys).filter(
    (k) => columnsCatalog.some((c) => c.key === k),
  );

  const activeColumns = columnsCatalog.filter((c) => activeKeys.includes(c.key));
  const totalWeight = activeColumns.reduce((sum, c) => sum + c.weight, 0) || 1;
  const widthFor = (weight: number) => `${Math.round((weight / totalWeight) * 100)}%`;

  const hasStatusColumn = activeColumns.some((c) => (c as any).isStatus);
  const legendItems: Array<{ symbol: string; color: string; label: string }> = hasStatusColumn
    ? [
      getStatusIcon(AttendanceSheetStatusEnum.EM_PREENCHIMENTO),
      getStatusIcon(AttendanceSheetStatusEnum.AGUARDANDO_ASSINATURA),
      getStatusIcon(AttendanceSheetStatusEnum.AGUARDANDO_REVISAO),
      getStatusIcon(AttendanceSheetStatusEnum.CONCLUIDA),
      getStatusIcon(AttendanceSheetStatusEnum.EM_CORRECAO),
    ]
    : [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={LogoDefensoria} style={styles.logo} />
          <View style={styles.headerText}>
            <Text style={styles.title}>DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE</Text>
            <Text style={styles.subtitle}>FOLHAS DE FREQUÊNCIA - {referencia}</Text>
            {showResumoLinha && (
              <Text style={styles.meta}>
                Registros {registrosVisiveis.length} de {totalRegistros}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            {activeColumns.map((col, idx) => {
              const isLast = idx === activeColumns.length - 1;
              return (
                <Text
                  key={col.key}
                  style={[
                    styles.cell,
                    { width: widthFor(col.weight) },
                    ...(isLast ? [styles.cellNoRightBorder] : []),
                  ]}
                >
                  {col.header}
                </Text>
              );
            })}
          </View>

          {registrosVisiveis.map((row, idx) => {
            const even = idx % 2 === 1;
            return (
              <View key={`${row.estagio.id}-${row.folha?.id ?? 'semfolha'}`} style={even ? styles.tableRowEven : styles.tableRow}>
                {activeColumns.map((col, colIdx) => {
                  const isLast = colIdx === activeColumns.length - 1;
                  const rawValue = (col as any).value(row);
                  const isStatus = (col as any).isStatus;

                  if (isStatus) {
                    const icon = rawValue as { symbol: string; color: string };
                    return (
                      <Text
                        key={col.key}
                        style={[
                          styles.cell,
                          { width: widthFor(col.weight) },
                          styles.statusIcon,
                          { color: icon.color },
                          ...(isLast ? [styles.cellNoRightBorder] : []),
                        ]}
                      >
                        {icon.symbol}
                      </Text>
                    );
                  }

                  return (
                    <Text
                      key={col.key}
                      style={[
                        styles.cell,
                        { width: widthFor(col.weight) },
                        ...(isLast ? [styles.cellNoRightBorder] : []),
                      ]}
                    >
                      {String(rawValue ?? '-')}
                    </Text>
                  );
                })}
              </View>
            );
          })}
        </View>

        {hasStatusColumn && (
          <View style={styles.legend}>
            <Text style={styles.legendTitle}>Legenda de status</Text>
            <View style={styles.legendRow}>
              {legendItems.map((item) => (
                <View key={item.symbol} style={styles.legendItem}>
                  <Text style={[styles.statusIcon, { color: item.color }]}>{item.symbol}</Text>
                  <Text>{item.label}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        <View style={styles.footer}>
          <Text>SGI - Sistema de Gestão Interna</Text>
          <Text>Gerado em: {new Date().toLocaleString()}</Text>
        </View>
      </Page>
    </Document>
  );
}

